/**
 * CoupleRoadmapMap — overlay full-screen con el mapa visual de las 12
 * estaciones de la ruta de terapia de pareja.
 *
 * Spec: docs/spec_roadmap_pareja_map.md §2, §2.1, §2.2, §3, §10, §11. DOM,
 * estados de estación, a11y (orden 1→12 en el DOM), foco y trap de Tab
 * replicados tal cual la spec — no re-derivados acá.
 *
 * v2 (2026-09-14, docs/spec_roadmap_pareja_map.md §14 +
 * comercial/BRIEF_MAPA_RUTA_PAREJA.md): `.roadmap-map__stations` pasa de
 * `column-reverse` a fondo ilustrado (`mapa.png`, tablero tipo snake) con los
 * 12 nodos en `position:absolute` por coordenadas fijas (`STATION_POSITIONS`,
 * medidas sobre la imagen real con grilla de referencia, mapeo arco=estación
 * 12 confirmado por el usuario — no recalcular). El orden DOM
 * 1→12 NO cambia — solo cambió el mecanismo de pintado visual. Se agrega el
 * marcador "estás acá" (mascota Konex, anima con Framer Motion `layout` al
 * cambiar `sesionActual`) y un badge de fecha con la próxima consulta de
 * pareja agendada (hoy vía `useRoadmapProgreso`).
 *
 * v3 (2026-09-28): el marcador pasa a ser la pareja del propio mapa (las
 * mascotas Ψ dorada + rosa, recortadas de `mapa.png` como ficha de juego — sin
 * asset nuevo, reusa la imagen ya cargada). Estado derivado en
 * `useRoadmapProgreso` (completadas vs. estación actual, ver ahí). Animación
 * de desbloqueo: al abrir el mapa con más progreso que el último visto en este
 * dispositivo (`localStorage`, clave por paciente), la pareja camina estación
 * por estación desde la última vista, cada candado salta al pasar y al final
 * aparece un banner "Estación N desbloqueada". Con reduced-motion se salta
 * directo al estado final.
 *
 * v4 (2026-09-28b): mapa nuevo (`mapa.jpeg`, 631×1600 — 11 monedas + arco +
 * plataforma de partida abajo) y la pareja deja de ser un recorte del fondo:
 * sprite propio `pareja.webp` (Konex + Vinka, pose saludando, fondo
 * transparente; fuente con las 3 poses en `comercial/creativos-roadmap-pareja/`
 * para la caminata animada a futuro). La plataforma de abajo es la partida
 * ("START", posición 0): la primera vez que el paciente abre el mapa en el
 * dispositivo la pareja arranca ahí y camina hasta su estación.
 *
 * Estación bloqueada: tap muestra un toast breve vía el `useToast()` compartido
 * de toda la app (`src/components/ui/ToastProvider.tsx`) — "mismo patrón que el
 * resto del sitio para acciones todavía no" (plan §7.7), nunca abre el detalle,
 * nunca setea `src` en un `<img>` para esa estación. Imágenes (carrusel) y
 * video de la tarjeta de detalle viven en `RoadmapStationMedia`.
 */

import { useCallback, useEffect, useRef, useState, type FC, type MouseEvent } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useToast } from '../../ui/ToastProvider';
import { RUTA_PAREJA, type RoadmapStation } from './roadmapSessions';
import RoadmapStationMedia from './RoadmapStationMedia';
import './CoupleRoadmapMap.css';

const MAPA_SRC = '/assets/img/12sesiones_parejas/mapa.jpeg';
const PAREJA_SRC = '/assets/img/12sesiones_parejas/pareja.webp';
const VISTO_KEY_PREFIX = 'roadmap_pareja_visto:';
/** Duración total máxima de la caminata — hops largos se acortan para no aburrir. */
const WALK_TOTAL_MS = 3600;
const HOP_MAX_MS = 650;
const HOP_MIN_MS = 280;

function readVisto(patientId: string | null): number | null {
  if (!patientId) return null;
  try {
    const raw = window.localStorage.getItem(VISTO_KEY_PREFIX + patientId);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeVisto(patientId: string | null, estacion: number): void {
  if (!patientId) return;
  try {
    window.localStorage.setItem(VISTO_KEY_PREFIX + patientId, String(estacion));
  } catch {
    /* storage bloqueado — la animación se repite, nada más */
  }
}

// Coordenadas medidas sobre mapa.jpeg (631×1600px, en % del ancho/alto) con
// marcas dibujadas sobre la imagen real: centro de la cara superior de cada
// moneda. 11 monedas (estaciones 1-11, de abajo hacia arriba) + el arco
// (estación 12, cierre/graduación). Posición 0 = plataforma de partida (START).
const START_POSITION = { left: 49.1, top: 89.5 };
const STATION_POSITIONS: Record<number, { left: number; top: number }> = {
  1: { left: 66.9, top: 74.7 },
  2: { left: 45.2, top: 67.8 },
  3: { left: 60.4, top: 60.8 },
  4: { left: 29.8, top: 55.8 },
  5: { left: 59.6, top: 49.3 },
  6: { left: 41.4, top: 42.6 },
  7: { left: 69.7, top: 36.8 },
  8: { left: 47.1, top: 29.4 },
  9: { left: 61.0, top: 23.8 },
  10: { left: 42.5, top: 19.7 },
  11: { left: 66.6, top: 15.2 },
  12: { left: 61.0, top: 9.4 },
};

function getStationPosition(numero: number): { left: number; top: number } {
  if (numero <= 0) return START_POSITION;
  return STATION_POSITIONS[numero] ?? STATION_POSITIONS[1];
}

/** Última estación vista en este dispositivo; 0 (START) si nunca abrió el mapa. */
function inicioCaminata(patientId: string | null, estacionActual: number): number {
  return Math.min(Math.max(readVisto(patientId) ?? 0, 0), estacionActual);
}

// Formato corto tipo "Jue 18 sep" — sin util compartido en el proyecto para
// este patrón exacto (ver formatShortDate en ClinicalRecords.tsx, que usa
// día+mes+año largo); se resuelve local acá porque es específico del badge.
function formatFechaCorta(iso: string): string {
  const raw = new Date(iso)
    .toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })
    .replace(/[,.]/g, '');
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export type EstacionStatus = 'completada' | 'actual' | 'bloqueada';

interface CoupleRoadmapMapProps {
  open: boolean;
  /** `false` mientras se refetchea el progreso — la animación espera datos frescos. */
  ready: boolean;
  completadas: number;
  estacionActual: number;
  total: number;
  fechaProximaConsulta: string | null;
  patientId: string | null;
  onClose: () => void;
  /** Foco vuelve acá al cerrar (X, Esc, tap en scrim) — spec §11. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

// `frontera` = hasta dónde llegó la pareja en pantalla (durante la caminata
// avanza de a una; en reposo es `estacionActual`). Todo lo que está más allá
// sigue con candado. Dentro de la frontera: completada si ya se hizo la
// sesión, "actual" la estación agendada (o la 1 si recién empieza).
function getEstadoEstacion(
  numero: number,
  completadas: number,
  estacionActual: number,
  frontera: number
): EstacionStatus {
  if (numero > frontera) return 'bloqueada';
  if (numero <= completadas) return 'completada';
  if (numero === estacionActual) return 'actual';
  return 'bloqueada';
}

const CoupleRoadmapMap: FC<CoupleRoadmapMapProps> = ({
  open,
  ready,
  completadas,
  estacionActual,
  total,
  fechaProximaConsulta,
  patientId,
  onClose,
  returnFocusRef,
}) => {
  const toast = useToast();
  const reduceMotion = useReducedMotion() === true;
  const panelRef = useRef<HTMLDivElement>(null);
  const stationRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const closeRef = useRef<HTMLButtonElement>(null);
  const detailCloseRef = useRef<HTMLButtonElement>(null);
  const lastStationButtonRef = useRef<HTMLButtonElement | null>(null);

  const [selected, setSelected] = useState<RoadmapStation | null>(null);
  // Estación donde se dibuja la pareja — avanza de a una durante la caminata.
  // Arranca en la última vista (no en la nueva) para que al abrir no haya un
  // salto hacia atrás antes de la caminata.
  const [frontera, setFrontera] = useState(() => inicioCaminata(patientId, estacionActual));
  const [justUnlocked, setJustUnlocked] = useState<number | null>(null);
  const [celebracion, setCelebracion] = useState<RoadmapStation | null>(null);
  const [walking, setWalking] = useState(false);

  const detailOpen = selected !== null;

  // Caminata + desbloqueo. Corre una vez por apertura, recién con datos
  // frescos (`ready`). Desde la última estación vista en este dispositivo
  // (o la partida START si nunca abrió el mapa) hasta `estacionActual`.
  useEffect(() => {
    if (!open) {
      setWalking(false);
      setJustUnlocked(null);
      setCelebracion(null);
      setFrontera(inicioCaminata(patientId, estacionActual));
      return;
    }
    if (!ready) return;

    const visto = readVisto(patientId);
    const desde = inicioCaminata(patientId, estacionActual);
    const hops = estacionActual - desde;
    const avanzo = visto === null || estacionActual > visto;

    // Llevar la vista a donde arranca la pareja: el mapa es alto y el scroll
    // arranca arriba (en el arco), con la pareja fuera de vista.
    const scrollRaf = window.requestAnimationFrame(() => {
      const inicio = !avanzo || hops <= 0 || reduceMotion ? estacionActual : desde;
      stationRefs.current[inicio]?.scrollIntoView({ block: 'center' }); // 0 = START
    });

    if (!avanzo || hops <= 0 || reduceMotion) {
      setFrontera(estacionActual);
      setWalking(false);
      if (avanzo) setCelebracion(RUTA_PAREJA[estacionActual - 1] ?? null);
      writeVisto(patientId, estacionActual);
      return () => window.cancelAnimationFrame(scrollRaf);
    }

    const hopMs = Math.min(HOP_MAX_MS, Math.max(HOP_MIN_MS, WALK_TOTAL_MS / hops));
    const timers: number[] = [];
    setFrontera(desde);
    setWalking(true);
    setCelebracion(null);

    // Pausa inicial para que el panel termine de entrar antes de caminar.
    for (let i = 1; i <= hops; i++) {
      timers.push(
        window.setTimeout(() => {
          const n = desde + i;
          setFrontera(n);
          setJustUnlocked(n);
          stationRefs.current[n]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 500 + i * hopMs)
      );
    }
    timers.push(
      window.setTimeout(() => {
        setWalking(false);
        setJustUnlocked(null);
        setCelebracion(RUTA_PAREJA[estacionActual - 1] ?? null);
        writeVisto(patientId, estacionActual);
      }, 500 + hops * hopMs + 450)
    );

    return () => {
      window.cancelAnimationFrame(scrollRaf);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [open, ready, estacionActual, patientId, reduceMotion]);

  const closeDetail = useCallback(() => {
    setSelected(null);
    lastStationButtonRef.current?.focus();
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    returnFocusRef?.current?.focus();
  }, [onClose, returnFocusRef]);

  const handleStationClick = useCallback(
    (station: RoadmapStation, status: EstacionStatus, e: MouseEvent<HTMLButtonElement>) => {
      if (status === 'bloqueada') {
        toast.info('Vas a llegar acá pronto');
        return;
      }
      lastStationButtonRef.current = e.currentTarget;
      setSelected(station);
    },
    [toast]
  );

  // Scroll-lock en AMBOS breakpoints — a diferencia de Konex, este overlay es
  // modal en desktop también (spec §11).
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const raf = window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(raf);
    };
  }, [open]);

  // Reset del detalle al cerrar el mapa completo.
  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  // Foco al abrir el detalle — mismo patrón que la tarjeta mobile de Konex.
  useEffect(() => {
    if (!detailOpen) return;
    const raf = window.requestAnimationFrame(() => detailCloseRef.current?.focus());
    return () => window.cancelAnimationFrame(raf);
  }, [detailOpen]);

  // Esc en 2 niveles (spec §2.2) + trap de Tab dentro de .roadmap-map__panel —
  // mismo algoritmo que KonexGuide.tsx.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (detailOpen) {
          closeDetail();
        } else {
          handleClose();
        }
        return;
      }

      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = getFocusable(panel);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, detailOpen, closeDetail, handleClose]);

  const handleScrim = useCallback(() => {
    // El scrim del mapa solo cierra el mapa — cerrar el detalle es un gesto
    // aparte (su propio X / Esc), spec §2.2.
    if (detailOpen) return;
    handleClose();
  }, [detailOpen, handleClose]);


  // Mismo número que el badge del botón flotante (estacionActual).
  const progresoLabel = `Sesión ${estacionActual} de ${total}`;

  const parejaPos = getStationPosition(Math.min(Math.max(frontera, 0), total));
  const siguiente = completadas + 1;
  const pista =
    completadas >= total
      ? '¡Completaron la ruta!'
      : !fechaProximaConsulta && siguiente <= total && completadas > 0
        ? `Agenda tu sesión ${siguiente} para desbloquear la siguiente estación`
        : null;

  return (
    <div className="roadmap-map" id="roadmap-map-dialog" data-state={open ? 'open' : 'closed'}>
      <div className="roadmap-map__scrim" aria-hidden="true" onClick={handleScrim} />

      <div
        ref={panelRef}
        className="roadmap-map__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="roadmap-map-title"
        aria-describedby="roadmap-map-progress"
      >
        <header className="roadmap-map__header">
          <div className="roadmap-map__heading">
            <h2 id="roadmap-map-title" className="roadmap-map__title">
              Ruta de tu terapia de pareja
            </h2>
            <p id="roadmap-map-progress" className="roadmap-map__progress">
              {progresoLabel}
              {completadas > 0 && ` · ${completadas} completada${completadas === 1 ? '' : 's'}`}
            </p>
            {pista && <p className="roadmap-map__hint">{pista}</p>}
          </div>
          <button
            ref={closeRef}
            className="roadmap-map__close"
            type="button"
            aria-label="Cerrar mapa de la ruta"
            onClick={handleClose}
          >
            <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="roadmap-map__path">
          <ol
            className="roadmap-map__stations"
            style={{ backgroundImage: `url('${MAPA_SRC}')` }}
            aria-label={`${total} estaciones de tu proceso de terapia de pareja`}
          >
            {RUTA_PAREJA.map((station) => {
              const status = getEstadoEstacion(station.numero, completadas, estacionActual, frontera);
              const isCompletada = status === 'completada';
              const isActual = status === 'actual';
              const isBloqueada = status === 'bloqueada';
              const label = isBloqueada
                ? `Estación ${station.numero} de ${total}: ${station.titulo} — bloqueada. Se desbloquea al llegar a esta sesión.`
                : `Estación ${station.numero} de ${total}: ${station.titulo} — ${
                    isCompletada ? 'completada' : 'estación actual'
                  }. Ver detalle`;

              const pos = getStationPosition(station.numero);

              return (
                <li
                  key={station.numero}
                  ref={(el) => {
                    stationRefs.current[station.numero] = el;
                  }}
                  className="roadmap-map__item"
                  data-status={status}
                  data-just-unlocked={justUnlocked === station.numero || undefined}
                  style={{ left: `${pos.left}%`, top: `${pos.top}%` }}
                >
                  <button
                    type="button"
                    className="roadmap-map__station"
                    aria-expanded={isBloqueada ? undefined : selected?.numero === station.numero}
                    aria-controls={isBloqueada ? undefined : 'roadmap-detail'}
                    aria-current={isActual ? 'step' : undefined}
                    aria-label={label}
                    onClick={(e) => handleStationClick(station, status, e)}
                  >
                    <span className="roadmap-map__node">
                      {isCompletada ? (
                        <svg
                          className="roadmap-map__check"
                          aria-hidden="true"
                          focusable="false"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M3 8l3.5 3.5L13 5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          />
                        </svg>
                      ) : isBloqueada ? (
                        <svg
                          className="roadmap-map__lock"
                          aria-hidden="true"
                          focusable="false"
                          viewBox="0 0 16 16"
                        >
                          <rect
                            x="3"
                            y="7"
                            width="10"
                            height="7"
                            rx="1.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                          <path
                            d="M5 7V5a3 3 0 016 0v2"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                        </svg>
                      ) : (
                        <span className="roadmap-map__number">{station.numero}</span>
                      )}
                    </span>
                    <span className="roadmap-map__label">
                      {isActual && <span className="roadmap-map__status-text">Estás acá</span>}
                      {!isActual && <span className="roadmap-map__number">{station.numero}</span>}
                      <span className="roadmap-map__title-text">{station.titulo}</span>
                    </span>
                  </button>
                </li>
              );
            })}

            {/* Plataforma de partida — decorativa, la pareja arranca acá la
                primera vez. */}
            <li
              aria-hidden="true"
              ref={(el) => {
                stationRefs.current[0] = el;
              }}
              className="roadmap-map__item roadmap-map__item--start"
              style={{ left: `${START_POSITION.left}%`, top: `${START_POSITION.top}%` }}
            >
              <span className="roadmap-map__start">START</span>
            </li>

            {/* Marcador "están acá" — la pareja (Konex + Vinka) como ficha de
                juego. Decorativo (aria-hidden): el estado real lo
                comunica el botón de la estación actual (aria-current="step").
                Anima left/top en % entre estaciones; con reduced-motion se
                resuelve sin transición (spec §5.4/§10.2). */}
            <motion.li
              aria-hidden="true"
              className="roadmap-map__item roadmap-map__item--pareja"
              data-walking={walking || undefined}
              initial={false}
              animate={{ left: `${parejaPos.left}%`, top: `${parejaPos.top}%` }}
              transition={
                reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 170, damping: 20 }
              }
            >
              <img
                className="roadmap-map__pareja-token"
                src={PAREJA_SRC}
                alt=""
                width={359}
                height={360}
                draggable={false}
              />
            </motion.li>

            {/* Badge de fecha — próxima consulta de pareja agendada, sobre la
                estación actual. Interpretación de la sesión 2026-09-14:
                "el check" = la estación a la que el paciente va, que es la
                que tiene la consulta ya agendada. Decorativo/redundante (la
                fecha también vive en "Próximas sesiones" del propio flujo),
                mismo criterio que el badge del botón flotante (§1 de la spec). */}
            {fechaProximaConsulta && !walking && frontera === estacionActual && (
              <li
                aria-hidden="true"
                className="roadmap-map__item roadmap-map__item--fecha"
                style={{ left: `${parejaPos.left}%`, top: `${parejaPos.top}%` }}
              >
                <span className="roadmap-map__fecha-badge">
                  {formatFechaCorta(fechaProximaConsulta)}
                </span>
              </li>
            )}
          </ol>
        </div>

        {celebracion && !detailOpen && (
          <div className="roadmap-map__unlock" role="status">
            <span className="roadmap-map__unlock-eyebrow">
              Estación {celebracion.numero}{' '}
              {celebracion.numero <= completadas ? 'completada' : 'desbloqueada'}
            </span>
            <span className="roadmap-map__unlock-title">{celebracion.titulo}</span>
            <div className="roadmap-map__unlock-actions">
              <button
                type="button"
                className="roadmap-map__unlock-cta"
                onClick={(e) => {
                  const btn = stationRefs.current[celebracion.numero]?.querySelector('button');
                  lastStationButtonRef.current = btn ?? e.currentTarget;
                  setSelected(celebracion);
                  setCelebracion(null);
                }}
              >
                Ver estación
              </button>
              <button
                type="button"
                className="roadmap-map__unlock-dismiss"
                aria-label="Cerrar aviso de estación desbloqueada"
                onClick={() => setCelebracion(null)}
              >
                Ahora no
              </button>
            </div>
          </div>
        )}

        <div
          className="roadmap-map__detail"
          id="roadmap-detail"
          role="region"
          aria-labelledby="roadmap-detail-title"
          data-open={String(detailOpen)}
        >
          <button
            ref={detailCloseRef}
            className="roadmap-map__detail-close"
            type="button"
            aria-label="Cerrar detalle de la estación"
            onClick={closeDetail}
          >
            <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {selected && (
            <>
              <RoadmapStationMedia key={selected.numero} station={selected} onClose={closeDetail} />
              <p className="roadmap-map__detail-eyebrow" id="roadmap-detail-title">
                Estación {selected.numero} · {selected.titulo}
              </p>
              {selected.descripcion && (
                <p className="roadmap-map__detail-text">{selected.descripcion}</p>
              )}
              {selected.enlace && (
                <a
                  className="roadmap-map__detail-cta"
                  href={selected.enlace.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selected.enlace.etiqueta}
                </a>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoupleRoadmapMap;
